/**
 * Selection Manager
 * Handles selection management and manipulation
 */

import { logger } from '../utils/logger'
import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'

export class SelectionManager {
  private apiService: ApiService
  private appState: AppState

  constructor(apiService: ApiService, appState: AppState) {
    this.apiService = apiService
    this.appState = appState
    logger.debug('SelectionManager initialized')
  }

  /**
   * Remove selection
   */
  public async removeSelection(selectionId: string): Promise<void> {
    try {
      logger.debug('Removing selection:', selectionId)
      
      await this.apiService.deleteSelection(selectionId)
      
      // Remove from app state
      const currentSelections = this.appState.getSelections()
      const updatedSelections = currentSelections.filter(sel => sel.id !== selectionId)
      this.appState.setSelections(updatedSelections)
      
      // CRITICAL FIX: Refresh project data to update selection_count
      const currentProject = this.appState.getCurrentProject()
      if (currentProject) {
        logger.debug('Refreshing project data to update selection_count after selection removal')
        try {
          const updatedProject = await this.apiService.getProject(currentProject.id)
          this.appState.setCurrentProject(updatedProject)
          logger.debug('Project selection_count updated after selection removal:', updatedProject.selection_count)
        } catch (error) {
          logger.error('Failed to refresh project data after selection removal:', error)
        }
      }
      
      logger.debug('Selection removed successfully:', selectionId)
    } catch (error) {
      logger.error('Failed to remove selection:', error)
      throw error
    }
  }

  /**
   * Edit selection
   */
  public async editSelection(selectionId: string): Promise<void> {
    try {
      logger.debug('Editing selection:', selectionId)
      
      const selection = this.appState.getSelections().find(s => s.id === selectionId)
      if (!selection) {
        throw new Error('Selection not found')
      }


      // Debug: Log the selection data to understand the structure
      console.log('🔍 SelectionManager.editSelection - Full selection data:', {
        id: selection.id,
        document_type: selection.document_type,
        document_id: selection.document_id,
        extracted_text: selection.extracted_text?.substring(0, 100) + '...',
        all_keys: Object.keys(selection)
      })

      // Handle manual text entries differently - enhanced detection
      const isManual = selection.document_type === 'manual' || 
                       selection.document_id === 'manual-entry'
      
      if (isManual) {
        logger.debug('Editing manual text entry:', selectionId)
        
        // Get the current text content
        const currentText = selection.extracted_text || selection.edited_text || ''
        
        // Open the manual entry modal with pre-filled text
        // Try multiple ways to access the modal manager
        let modalManager = (window as any).appInstance?.modalManager
        
        // Fallback: try to get it from the global app instance
        if (!modalManager && (window as any).app) {
          modalManager = (window as any).app.modalManager
        }
        
        // Fallback: try to get it from the app state
        if (!modalManager && this.appState) {
          modalManager = (this.appState as any).modalManager
        }
        
        // Fallback: try to get it from the step renderer
        if (!modalManager && (window as any).stepRenderer) {
          modalManager = (window as any).stepRenderer._modalManager
        }
        
        if (modalManager) {
          logger.debug('Opening manual entry modal for editing with text:', currentText.substring(0, 100) + '...')
          await modalManager.openManualEntryModal(currentText, selection.document_id)
        } else {
          logger.error('ModalManager not available for editing manual text entry')
          // Fallback: dispatch a custom event to open the modal
          const event = new CustomEvent('open-manual-entry-modal', {
            detail: { 
              prefilledText: currentText, 
              documentId: selection.document_id,
              isEditing: true 
            }
          })
          window.dispatchEvent(event)
        }
      } else if (selection.document_type === 'image') {
        // Handle image document selections
        logger.debug('Image selection edit requested for:', selectionId)
        
        // Get the image document for this selection
        const documents = this.appState.getProjectDocuments()
        const imageDoc = documents.find(doc => doc.id === selection.document_id)
        
        if (!imageDoc) {
          logger.error('Image document not found for selection:', selectionId)
          throw new Error('Image document not found for this selection')
        }
        
        // Try multiple ways to access the modal manager
        let modalManager = (window as any).appInstance?.modalManager
        
        // Fallback: try to get it from the global app instance
        if (!modalManager && (window as any).app) {
          modalManager = (window as any).app.modalManager
        }
        
        // Fallback: try to get it from the app state
        if (!modalManager && this.appState) {
          modalManager = (this.appState as any).modalManager
        }
        
        // Fallback: try to get it from the step renderer
        if (!modalManager && (window as any).stepRenderer) {
          modalManager = (window as any).stepRenderer._modalManager
        }
        
        if (modalManager) {
          // First open the document upload modal to create the containers
          modalManager.openDocumentUploadModal()
          
          // Store the selection ID for highlighting after image loads
          ;(window as any).editingSelectionId = selectionId
          
          // Initialize image viewer in edit mode
          try {
            await modalManager.initializeModalImageViewer(imageDoc, false, true)
            logger.debug('Image viewer opened in edit mode for selection:', selectionId)
          } catch (error) {
            logger.error('Failed to initialize image viewer for editing:', error)
          }
        } else {
          logger.error('ModalManager not available for editing image selection')
          throw new Error('Cannot open image viewer for editing')
        }
      } else {
        // For regular PDF selections, open the PDF viewer in edit mode
        logger.debug('Regular selection edit requested for:', selectionId)
        
        // Get the PDF file for this selection
        const documents = this.appState.getProjectDocuments()
        const pdfDoc = documents.find(doc => doc.id === selection.document_id)
        
        if (!pdfDoc) {
          logger.error('PDF document not found for selection:', selectionId)
          throw new Error('PDF document not found for this selection')
        }
        
        const pdfFile = {
          id: pdfDoc.id,
          project_id: this.appState.getCurrentProject()?.id || '',
          filename: pdfDoc.original_filename,
          pageCount: pdfDoc.page_count || 1,
          dimensions: { width: 0, height: 0 }
        }
        
        // Open the document upload modal first, then initialize PDF viewer
        const modalManager = (window as any).appInstance?.modalManager || 
                           (window as any).app?.modalManager ||
                           (this.appState as any).modalManager ||
                           (window as any).stepRenderer?._modalManager
        
        if (modalManager) {
          // First open the document upload modal to create the containers
          modalManager.openDocumentUploadModal()
          
          // Store the selection ID for highlighting after PDF loads
          ;(window as any).editingSelectionId = selectionId
          
          // Initialize PDF viewer immediately - no delay needed
          try {
            await modalManager.initializeModalPDFViewer(pdfFile, true, false)
            logger.debug('PDF viewer opened in edit mode for selection:', selectionId)
          } catch (error) {
            logger.error('Failed to initialize PDF viewer for editing:', error)
          }
        } else {
          logger.error('ModalManager not available for editing PDF selection')
          throw new Error('Cannot open PDF viewer for editing')
        }
      }
    } catch (error) {
      logger.error('Failed to edit selection:', error)
      throw error
    }
  }

  /**
   * Update selection
   */
  public async updateSelection(selectionId: string, updates: any): Promise<void> {
    try {
      logger.debug('Updating selection:', selectionId, 'with updates:', Object.keys(updates))
      
      await this.apiService.updateSelection(selectionId, updates)
      
      // Update app state
      const selections = this.appState.getSelections()
      const updatedSelections = selections.map(sel => 
        sel.id === selectionId ? { ...sel, ...updates } : sel
      )
      this.appState.setSelections(updatedSelections)
      
      logger.debug('Selection updated successfully:', selectionId)
    } catch (error) {
      logger.error('Failed to update selection:', error)
      throw error
    }
  }

  /**
   * Create new selection
   */
  public async createSelection(selectionData: any): Promise<any> {
    try {
      logger.debug('Creating new selection:', selectionData)
      
      const response = await this.apiService.createSelection(selectionData)
      
      // Add to app state
      const currentSelections = this.appState.getSelections()
      this.appState.setSelections([...currentSelections, response])
      
      // 🚀 BACKGROUND OCR: Trigger immediately after selection creation
      if (response.thumbnail_data && response.thumbnail_data !== 'loading' && response.thumbnail_data !== 'error') {
        this.triggerBackgroundOCR(response.id)
      }
      
      logger.debug('Selection created successfully:', response.id)
      return response
    } catch (error) {
      logger.error('Failed to create selection:', error)
      throw error
    }
  }

  /**
   * Get selection by ID
   */
  public getSelectionById(selectionId: string): any {
    const selections = this.appState.getSelections()
    return selections.find(sel => sel.id === selectionId)
  }

  /**
   * Get selections by PDF ID
   */
  public getSelectionsByPDFId(pdfId: string): any[] {
    const selections = this.appState.getSelections()
    return selections.filter(sel => sel.document_id === pdfId)
  }

  /**
   * Get selections by project ID
   */
  public getSelectionsByProjectId(projectId: string): any[] {
    const selections = this.appState.getSelections()
    return selections.filter(sel => sel.project_id === projectId)
  }

  /**
   * Update selection text
   */
  public async updateSelectionText(selectionId: string, text: string, isEdited: boolean = false): Promise<void> {
    const updates = isEdited 
      ? { edited_text: text }
      : { extracted_text: text }
    
    await this.updateSelection(selectionId, updates)
  }

  /**
   * Update selection confidence
   */
  public async updateSelectionConfidence(selectionId: string, confidence: number): Promise<void> {
    await this.updateSelection(selectionId, { confidence_score: confidence })
  }

  /**
   * Update selection position
   */
  public async updateSelectionPosition(selectionId: string, position: any): Promise<void> {
    await this.updateSelection(selectionId, { position })
  }

  /**
   * Get selection statistics
   */
  public getSelectionStatistics(): any {
    const selections = this.appState.getSelections()
    
    const stats = {
      totalSelections: selections.length,
      averageConfidence: 0,
      confidenceDistribution: {
        high: 0,    // > 80%
        medium: 0,  // 50-80%
        low: 0      // < 50%
      },
      textLengthStats: {
        average: 0,
        min: 0,
        max: 0
      },
      manualEntries: 0,
      ocrEntries: 0
    }
    
    if (selections.length === 0) {
      return stats
    }
    
    // Calculate confidence statistics
    const confidences = selections.map(s => (s as any).confidence_score || 0)
    stats.averageConfidence = confidences.reduce((sum, conf) => sum + conf, 0) / confidences.length
    
    // Confidence distribution
    selections.forEach(sel => {
      const conf = (sel as any).confidence_score || 0
      if (conf > 80) stats.confidenceDistribution.high++
      else if (conf >= 50) stats.confidenceDistribution.medium++
      else stats.confidenceDistribution.low++
    })
    
    // Text length statistics
    const textLengths = selections.map(s => (s.edited_text || s.extracted_text || '').length)
    stats.textLengthStats.average = textLengths.reduce((sum, len) => sum + len, 0) / textLengths.length
    stats.textLengthStats.min = Math.min(...textLengths)
    stats.textLengthStats.max = Math.max(...textLengths)
    
    // Manual vs OCR entries
    selections.forEach(sel => {
      if ((sel as any).is_manual_entry) {
        stats.manualEntries++
      } else {
        stats.ocrEntries++
      }
    })
    
    return stats
  }

  /**
   * Validate selection data
   */
  public validateSelectionData(selection: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    
    if (!selection.id) {
      errors.push('Selection ID is required')
    }
    
    if (!selection.project_id) {
      errors.push('Project ID is required')
    }
    
    if (!selection.extracted_text && !selection.edited_text) {
      errors.push('Selection must have either extracted_text or edited_text')
    }
    
    if (selection.confidence_score !== undefined && (selection.confidence_score < 0 || selection.confidence_score > 100)) {
      errors.push('Confidence score must be between 0 and 100')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Bulk update selections
   */
  public async bulkUpdateSelections(updates: Array<{ id: string; updates: any }>): Promise<void> {
    try {
      logger.debug('Bulk updating selections:', updates.length)
      
      const results = await Promise.allSettled(
        updates.map(({ id, updates: selectionUpdates }) => 
          this.updateSelection(id, selectionUpdates)
        )
      )
      
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      
      logger.debug(`Bulk update completed: ${successful} successful, ${failed} failed`)
      
      if (failed > 0) {
        logger.warn(`${failed} selections failed to update`)
      }
    } catch (error) {
      logger.error('Failed to bulk update selections:', error)
      throw error
    }
  }

  /**
   * Search selections
   */
  public searchSelections(query: string, options: any = {}): any[] {
    const selections = this.appState.getSelections()
    const { 
      searchIn = ['extracted_text', 'edited_text'],
      caseSensitive = false,
      exactMatch = false
    } = options
    
    const searchQuery = caseSensitive ? query : query.toLowerCase()
    
    return selections.filter(selection => {
    return searchIn.some((field: string) => {
      const text = (selection as any)[field] || ''
        const searchText = caseSensitive ? text : text.toLowerCase()
        
        if (exactMatch) {
          return searchText === searchQuery
        } else {
          return searchText.includes(searchQuery)
        }
      })
    })
  }

  /**
   * Trigger background OCR processing for a selection
   */
  public async triggerBackgroundOCR(selectionId: string): Promise<void> {
    try {
      logger.debug('Triggering background OCR for selection:', selectionId)
      
      // Update UI immediately to show OCR is starting
      this.appState.updateOCRStatus(selectionId, {
        status: 'processing',
        startedAt: new Date()
      })
      
      // Start OCR processing (non-blocking)
      const response = await this.apiService.startOCRProcessing(selectionId)
      
      if (response.status === 'started') {
        // Poll for completion
        this.pollForOCRCompletion(selectionId)
      } else if (response.status === 'already_processing') {
        // Already processing, just poll for completion
        this.pollForOCRCompletion(selectionId)
      } else if (response.status === 'queue_full') {
        // Queue is full, retry later
        this.appState.updateOCRStatus(selectionId, {
          status: 'pending',
          error: 'OCR queue is full, will retry automatically'
        })
        
        // Retry after a delay
        setTimeout(() => {
          this.triggerBackgroundOCR(selectionId)
        }, 5000)
      }
      
    } catch (error) {
      logger.error('Failed to trigger background OCR:', error)
      this.appState.updateOCRStatus(selectionId, {
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to start OCR processing'
      })
    }
  }
  
  /**
   * Poll for OCR completion
   */
  private async pollForOCRCompletion(selectionId: string): Promise<void> {
    const maxAttempts = 30 // 30 seconds max
    let attempts = 0
    
    while (attempts < maxAttempts) {
      try {
        const status = await this.apiService.getOCRStatus(selectionId)
        
        if (status.ocr_status === 'completed') {
          this.appState.updateOCRStatus(selectionId, {
            status: 'completed',
            completedAt: new Date()
          })
          
          // Use a more graceful update that doesn't trigger full re-renders
          this.appState.updateSelectionInPlace(selectionId, {
            extracted_text: status.extracted_text,
            ocr_confidence: status.ocr_confidence
          })
          
          logger.debug('OCR completed for selection:', selectionId)
          return
        } else if (status.ocr_status === 'failed') {
          this.appState.updateOCRStatus(selectionId, {
            status: 'failed',
            error: status.error || 'OCR processing failed'
          })
          logger.error('OCR failed for selection:', selectionId, status.error)
          return
        }
        
        // Still processing, wait and try again
        await new Promise(resolve => setTimeout(resolve, 1000))
        attempts++
        
      } catch (error) {
        logger.error('Error polling OCR status:', error)
        this.appState.updateOCRStatus(selectionId, {
          status: 'failed',
          error: `Polling error: ${error instanceof Error ? error.message : 'Unknown error'}`
        })
        return
      }
    }
    
    // Timeout
    this.appState.updateOCRStatus(selectionId, {
      status: 'failed',
      error: 'OCR processing timeout'
    })
    logger.error('OCR timeout for selection:', selectionId)
  }
  
  /**
   * Retry OCR processing for a failed selection
   */
  public async retryOCRProcessing(selectionId: string): Promise<void> {
    try {
      logger.debug('Retrying OCR processing for selection:', selectionId)
      
      // Reset status to pending
      this.appState.updateOCRStatus(selectionId, {
        status: 'pending'
      })
      
      // Start OCR processing
      await this.triggerBackgroundOCR(selectionId)
      
    } catch (error) {
      logger.error('Failed to retry OCR processing:', error)
      this.appState.updateOCRStatus(selectionId, {
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to retry OCR processing'
      })
    }
  }
  
  /**
   * Get OCR status for a selection
   */
  public getOCRStatus(selectionId: string): any {
    return this.appState.getOCRStatus(selectionId)
  }
  
  /**
   * Get all OCR statuses
   */
  public getAllOCRStatuses(): any[] {
    return this.appState.getAllOCRStatuses()
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    logger.debug('SelectionManager cleaned up')
  }
}
